import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useLocation } from 'react-router-dom';
import CourseManagement from './CourseManagement';
import QuestionPackageManagement from './QuestionPackageManagement';
import AITestCaseGenerator from './AITestCaseGenerator';
import AIInterviewConfigAdmin from './AIInterviewConfigAdmin';
import AdminEnrollments from './AdminEnrollments';
import AdminInternshipRegistrations from './AdminInternshipRegistrations';
import UsersManagement from './UsersManagement';
import QuestionBank from './QuestionBank';
import ContactMessages from './ContactMessages';
import VisitorStats from './VisitorStats';
// SECURITY & MONITORING - COMMENTED OUT FOR NOW
// import ActivityMonitoring from './ActivityMonitoring';
import usePreventScroll from '../hooks/usePreventScroll';
import api from '../services/api';
import './AdminPanel.css';

const AdminPanel = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get initial tab from URL hash or default to 'articles'
    const getInitialTab = () => {
        const hash = location.hash.replace('#', '');
        const validTabs = ['articles', 'courses', 'question-packages', 'question-bank', 'ai-tools', 'enrollments', 'internship-registrations', 'users', 'contact-messages', 'visitor-stats', 'activity-monitoring'];
        return validTabs.includes(hash) ? hash : 'articles';
    };
    
    const [pendingArticles, setPendingArticles] = useState([]);
    const [allArticles, setAllArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [previewArticle, setPreviewArticle] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // Prevent background scrolling when preview modal is open
    usePreventScroll(showPreview);
    
    // Handle tab change with URL hash update
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        window.location.hash = tab;
    };

    useEffect(() => {
        console.log('AdminPanel: Starting to load articles...');
        loadArticles();
        
        // Safety timeout: if loading takes more than 30 seconds, show error
        const timeout = setTimeout(() => {
            if (loading) {
                console.error('Admin panel loading timeout - API calls took longer than 30 seconds');
                console.error('Check Network tab: Are requests to /api/articles/pending and /api/articles/admin successful?');
                setMessage('⚠️ Loading timeout - Backend API is slow or unreachable. Check browser console for details.');
                setLoading(false);
            }
        }, 30000);
        
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadArticles = async () => {
        console.log('loadArticles: Starting API calls...');
        setLoading(true);
        try {
            console.log('loadArticles: Fetching pending and admin articles in parallel...');
            // Fetch both in parallel
            const [pendingRes, allRes] = await Promise.all([
                api.get('/api/articles/pending').catch(err => {
                    console.error('❌ Error fetching pending articles:', err);
                    console.error('Response:', err.response);
                    return { data: [] };
                }),
                api.get('/api/articles/admin').catch(err => {
                    console.error('❌ Error fetching all articles:', err);
                    console.error('Response:', err.response);
                    return { data: [] };
                })
            ]);
            
            console.log('✅ loadArticles: Got responses', {
                pending: pendingRes.data?.length || 0,
                all: allRes.data?.length || 0
            });
            
            setPendingArticles(pendingRes.data);
            setAllArticles(allRes.data);
        } catch (error) {
            console.error('❌ loadArticles: Unexpected error:', error);
        } finally {
            console.log('✅ loadArticles: Setting loading to false');
            setLoading(false);
        }
    };

    const fetchPendingArticles = async () => {
        try {
            const response = await api.get('/api/articles/pending');
            setPendingArticles(response.data);
        } catch (error) {
            console.error('Error fetching pending articles:', error);
        }
    };

    const fetchAllArticles = async () => {
        try {
            const response = await api.get('/api/articles/admin');
            setAllArticles(response.data);
        } catch (error) {
            console.error('Error fetching all articles:', error);
        }
    };

    const handleApprove = async (articleId) => {
        try {
            await api.post(`/api/articles/${articleId}/approve`);
            setMessage('✅ Article approved successfully!');
            fetchPendingArticles();
            fetchAllArticles();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Approval error:', error);
            setMessage('❌ Error approving article: ' + (error.response?.data?.message || error.message));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleReject = async (articleId) => {
        try {
            await api.post(`/api/articles/${articleId}/reject`);
            setMessage('✅ Article rejected successfully!');
            fetchPendingArticles();
            fetchAllArticles();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Rejection error:', error);
            setMessage('❌ Error rejecting article: ' + (error.response?.data?.message || error.message));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleDeleteArticle = async (articleId) => {
        if (!window.confirm('Are you sure you want to delete this article? This action cannot be undone.')) {
            return;
        }
        
        try {
            await api.delete(`/api/articles/${articleId}`);
            setMessage('✅ Article deleted successfully!');
            fetchPendingArticles();
            fetchAllArticles();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Delete error:', error);
            setMessage('❌ Error deleting article: ' + (error.response?.data?.message || error.message));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handlePreview = (article) => {
        setPreviewArticle(article);
        setShowPreview(true);
    };

    const closePreview = () => {
        setShowPreview(false);
        setPreviewArticle(null);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { icon: '⏳', color: 'orange', label: 'Pending Review' },
            'APPROVED': { icon: '✅', color: 'green', label: 'Approved' },
            'REJECTED': { icon: '❌', color: 'red', label: 'Rejected' }
        };
        
        const config = statusConfig[status] || { icon: '❓', color: 'gray', label: status };
        
        return (
            <span className={`status-badge ${config.color}`}>
                {config.icon} {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="admin-panel">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Loading admin panel...</div>
                    <div style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
                        If this takes too long, check browser console (F12) for errors
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-panel">
            <div className="admin-header">
                <h2>🚀 Admin Dashboard</h2>
                <p className="admin-subtitle">Manage your platform content and users</p>
            </div>
            
            {message && (
                <div className="message">
                    {message}
                </div>
            )}

            <div className="admin-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
                    onClick={() => handleTabChange('articles')}
                >
                    📝 Article Management
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
                    onClick={() => handleTabChange('courses')}
                >
                    🎓 Course Management
                </button>
                <button
                    className={`tab-btn ${activeTab === 'question-packages' ? 'active' : ''}`}
                    onClick={() => handleTabChange('question-packages')}
                >
                    📦 Question Packages
                </button>
                <button
                    className={`tab-btn ${activeTab === 'question-bank' ? 'active' : ''}`}
                    onClick={() => handleTabChange('question-bank')}
                >
                    📚 Question Bank
                </button>
                <button
                    className={`tab-btn ${activeTab === 'ai-tools' ? 'active' : ''}`}
                    onClick={() => handleTabChange('ai-tools')}
                >
                    🤖 AI Tools
                </button>
                <button
                    className={`tab-btn ${activeTab === 'enrollments' ? 'active' : ''}`}
                    onClick={() => handleTabChange('enrollments')}
                >
                    📋 Enrollments
                </button>
                <button
                    className={`tab-btn ${activeTab === 'internship-registrations' ? 'active' : ''}`}
                    onClick={() => handleTabChange('internship-registrations')}
                >
                    💻 DSA Crash
                </button>
                <button
                    className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => handleTabChange('users')}
                >
                    👥 Users
                </button>
                <button
                    className={`tab-btn ${activeTab === 'contact-messages' ? 'active' : ''}`}
                    onClick={() => handleTabChange('contact-messages')}
                >
                    📧 Contact Messages
                </button>
                <button
                    className={`tab-btn ${activeTab === 'visitor-stats' ? 'active' : ''}`}
                    onClick={() => handleTabChange('visitor-stats')}
                >
                    📊 Visitor Stats
                </button>
                {/* SECURITY & MONITORING - COMMENTED OUT FOR NOW */}
                {/* <button
                    className={`tab-btn ${activeTab === 'activity-monitoring' ? 'active' : ''}`}
                    onClick={() => handleTabChange('activity-monitoring')}
                >
                    🔒 Security & Monitoring
                </button> */}
            </div>
            
            {/* Quick Link to Recordings Management */}
            <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
                <button 
                    onClick={() => navigate('/admin/recordings')}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                    👻 Manage Ghost Coding Recordings
                </button>
            </div>

            <div style={{ display: activeTab === 'articles' ? 'block' : 'none' }}>
                <div className="admin-sections">
                    <div className="section">
                        <h3>⏳ Pending Articles ({pendingArticles.length})</h3>
                        {pendingArticles.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📭</div>
                                <p>No pending articles to review</p>
                                <small>All articles have been processed</small>
                            </div>
                        ) : (
                            <div className="articles-grid">
                                {pendingArticles.map(article => (
                                    <div key={article.id} className="article-card">
                                        <div className="article-header">
                                            <h4>{article.title}</h4>
                                            {getStatusBadge(article.status)}
                                        </div>
                                        <div className="article-meta">
                                            <p><strong>👤 Author:</strong> {article.author}</p>
                                            <p><strong>📅 Created:</strong> {new Date(article.createdAt).toLocaleDateString()}</p>
                                            <p><strong>⏱️ Read Time:</strong> {article.readTimeMinutes || 'N/A'} min</p>
                                        </div>
                                        <div className="article-actions">
                                            <button 
                                                className="preview-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handlePreview(article);
                                                }}
                                                title="Preview this article"
                                                style={{ pointerEvents: 'auto', zIndex: 10 }}
                                            >
                                                👁️ Preview
                                            </button>
                                            <button 
                                                className="approve-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleApprove(article.id);
                                                }}
                                                title="Approve this article"
                                                style={{ pointerEvents: 'auto', zIndex: 10 }}
                                            >
                                                ✅ Approve
                                            </button>
                                            <button 
                                                className="reject-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleReject(article.id);
                                                }}
                                                title="Reject this article"
                                                style={{ pointerEvents: 'auto', zIndex: 10 }}
                                            >
                                                ❌ Reject
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="section">
                        <h3>📚 All Articles ({allArticles.length})</h3>
                        <div className="articles-grid">
                            {allArticles.map(article => (
                                <div key={article.id} className="article-card">
                                    <div className="article-header">
                                        <h4>{article.title}</h4>
                                        {getStatusBadge(article.status)}
                                    </div>
                                    <div className="article-meta">
                                        <p><strong>👤 Author:</strong> {article.author}</p>
                                        <p><strong>📅 Created:</strong> {new Date(article.createdAt).toLocaleDateString()}</p>
                                        <p><strong>⏱️ Read Time:</strong> {article.readTimeMinutes || 'N/A'} min</p>
                                    </div>
                                    <div className="article-actions">
                                        <button 
                                            className="preview-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handlePreview(article);
                                            }}
                                            title="Preview this article"
                                            style={{ pointerEvents: 'auto', zIndex: 10 }}
                                        >
                                            👁️ Preview
                                        </button>
                                        {article.status === 'PENDING' && (
                                            <>
                                                <button 
                                                    className="approve-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleApprove(article.id);
                                                    }}
                                                    title="Approve this article"
                                                    style={{ pointerEvents: 'auto', zIndex: 10 }}
                                                >
                                                    ✅ Approve
                                                </button>
                                                <button 
                                                    className="reject-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleReject(article.id);
                                                    }}
                                                    title="Reject this article"
                                                    style={{ pointerEvents: 'auto', zIndex: 10 }}
                                                >
                                                    ❌ Reject
                                                </button>
                                            </>
                                        )}
                                        <button 
                                            className="delete-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDeleteArticle(article.id);
                                            }}
                                            title="Delete this article permanently"
                                            style={{ pointerEvents: 'auto', zIndex: 10 }}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: activeTab === 'courses' ? 'block' : 'none' }}>
                <CourseManagement />
            </div>

            <div style={{ display: activeTab === 'question-packages' ? 'block' : 'none' }}>
                <QuestionPackageManagement />
            </div>

            <div style={{ display: activeTab === 'question-bank' ? 'block' : 'none' }}>
                <QuestionBank />
            </div>

            <div style={{ display: activeTab === 'ai-tools' ? 'block' : 'none' }}>
                <AITestCaseGenerator />
                <AIInterviewConfigAdmin />
            </div>

            <div style={{ display: activeTab === 'enrollments' ? 'block' : 'none' }}>
                <AdminEnrollments />
            </div>

            <div style={{ display: activeTab === 'internship-registrations' ? 'block' : 'none' }}>
                <AdminInternshipRegistrations isActive={activeTab === 'internship-registrations'} />
            </div>

            <div style={{ display: activeTab === 'users' ? 'block' : 'none' }}>
                <UsersManagement />
            </div>

            <div style={{ display: activeTab === 'contact-messages' ? 'block' : 'none' }}>
                <ContactMessages />
            </div>

            <div style={{ display: activeTab === 'visitor-stats' ? 'block' : 'none' }}>
                <VisitorStats />
            </div>

            {/* SECURITY & MONITORING - COMMENTED OUT FOR NOW */}
            {/* <div style={{ display: activeTab === 'activity-monitoring' ? 'block' : 'none' }}>
                <ActivityMonitoring />
            </div> */}

            {/* Article Preview Modal */}
            {showPreview && previewArticle && (
                <div className="preview-modal-overlay" onClick={closePreview}>
                    <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>Article Preview</h3>
                            <button className="close-btn" onClick={closePreview}>×</button>
                        </div>
                        <div className="preview-content">
                            <div className="preview-meta">
                                <h2>{previewArticle.title}</h2>
                                <div className="preview-details">
                                    <span><strong>Author:</strong> {previewArticle.author}</span>
                                    <span><strong>Created:</strong> {new Date(previewArticle.createdAt).toLocaleDateString()}</span>
                                    <span><strong>Read Time:</strong> {previewArticle.readTimeMinutes || 'N/A'} min</span>
                                    <span><strong>Status:</strong> {getStatusBadge(previewArticle.status)}</span>
                                </div>
                            </div>
                            <div className="preview-body">
                                <h4>Content:</h4>
                                <div className="article-content">
                                  <div className="text-gray-200 leading-relaxed text-sm">
                                    {/* Render code blocks separately if they exist */}
                                    {previewArticle.codeBlocks && (
                                      <div className="mb-4">
                                        {JSON.parse(previewArticle.codeBlocks).map((block, index) => (
                                          <div key={index} className="bg-gray-900 rounded-lg border border-gray-300 p-4 my-6 overflow-x-auto max-w-full shadow-lg">
                                            <div className="text-teal-400 text-sm font-medium mb-3 flex items-center">
                                              <span className="bg-teal-400 text-gray-900 px-2 py-1 rounded text-xs font-bold mr-2">{block.language}</span>
                                              <span>Code Block</span>
                                            </div>
                                            <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap break-words max-w-full leading-relaxed">
                                              <code>{block.content}</code>
                                            </pre>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    
                                    <ReactMarkdown
                                        components={{
                                            code({ node, inline, className, children, ...props }) {
                                                const match = /language-(\w+)/.exec(className || '');
                                                return !inline && match ? (
                                                    <div className="bg-gray-900 rounded-lg border border-gray-300 p-4 my-6 overflow-x-auto max-w-full shadow-lg">
                                                        <div className="text-teal-400 text-sm font-medium mb-3 flex items-center">
                                                            <span className="bg-teal-400 text-gray-900 px-2 py-1 rounded text-xs font-bold mr-2">{match[1]}</span>
                                                            <span>Code Block</span>
                                                        </div>
                                                        <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap break-words max-w-full leading-relaxed">
                                                            <code {...props}>{String(children).replace(/\n$/, '')}</code>
                                                        </pre>
                                                    </div>
                                                ) : (
                                                    <code className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono border" {...props}>
                                                        {children}
                                                    </code>
                                                );
                                            },
                                            h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-8 break-words border-b border-gray-300 pb-2">{children}</h1>,
                                            h2: ({ children }) => <h2 className="text-xl font-bold text-gray-700 mb-3 mt-6 break-words">{children}</h2>,
                                            h3: ({ children }) => <h3 className="text-lg font-bold text-gray-600 mb-2 mt-4 break-words">{children}</h3>,
                                            p: ({ children }) => <p className="text-gray-700 mb-6 leading-relaxed text-base break-words">{children}</p>,
                                            ul: ({ children }) => <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 text-base break-words pl-6">{children}</ul>,
                                            ol: ({ children }) => <ol className="list-decimal list-inside text-gray-700 mb-6 space-y-2 text-base break-words pl-6">{children}</ol>,
                                            li: ({ children }) => <li className="text-gray-700 text-base break-words leading-relaxed">{children}</li>,
                                            blockquote: ({ children }) => (
                                                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-6 text-base break-words bg-gray-50 p-4 rounded-r-lg">
                                                    {children}
                                                </blockquote>
                                            ),
                                            strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                                            em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
                                            img: ({ src, alt }) => (
                                                <img 
                                                    src={src} 
                                                    alt={alt} 
                                                    className="rounded-lg border border-gray-300 my-3 max-w-full h-auto"
                                                />
                                            ),
                                            a: ({ href, children }) => (
                                                <a 
                                                    href={href} 
                                                    className="text-teal-600 hover:text-teal-700 underline text-sm"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {children}
                                                </a>
                                            ),
                                        }}
                                    >
                                        {previewArticle.content}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                            </div>
                            <div className="preview-actions">
                                <button 
                                    className="approve-btn"
                                    onClick={() => {
                                        handleApprove(previewArticle.id);
                                        closePreview();
                                    }}
                                >
                                    ✅ Approve
                                </button>
                                <button 
                                    className="reject-btn"
                                    onClick={() => {
                                        handleReject(previewArticle.id);
                                        closePreview();
                                    }}
                                >
                                    ❌ Reject
                                </button>
                                <button 
                                    className="close-btn-secondary"
                                    onClick={closePreview}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel; 
